import type { CanonicalPostAuthor, PostAuthorAttribution } from "./postPublishing";

export const POST_PUBLISH_SCHEMA_VERSION = 1 as const;

export type PostPublishStatus =
  | "preparing"
  | "queued"
  | "uploading"
  | "committing"
  | "awaiting-moderation"
  | "published"
  | "failed"
  | "canceled";

export type PostPublishAttachmentStatus =
  | "preparing"
  | "ready"
  | "uploading"
  | "uploaded"
  | "failed";

export type SerializedPostPublishError = {
  code: string;
  message: string;
  retryable: boolean;
  stage: string;
  httpStatus?: number;
};

export type PostPublishStorageResult = {
  url?: string;
  storagePath: string;
  contentType: string;
  width?: number;
  height?: number;
  name?: string;
};

export type PostPublishAttachment = {
  attachmentId: string;
  order: number;
  type: "image" | "pdf";
  status: PostPublishAttachmentStatus;
  durableUri: string;
  previewUri: string;
  name: string;
  contentType: "image/jpeg" | "application/pdf";
  originalSize?: number;
  normalizedSize: number;
  width?: number;
  height?: number;
  preparationDurationMs?: number;
  uploadDurationMs?: number;
  storage?: PostPublishStorageResult;
  error?: SerializedPostPublishError;
};

export type PostPublishGroupSnapshot = {
  groupId: string;
  groupName: string;
  isPublic: boolean;
};

export type PostPublishProgress = {
  completed: number;
  total: number;
  fraction: number;
};

export type PostPublishJob = {
  schemaVersion: typeof POST_PUBLISH_SCHEMA_VERSION;
  jobId: string;
  postId: string;
  /** Immutable account binding for this job. */
  readonly authorUid: string;
  /** @deprecated v1 compatibility alias. New jobs must use authorUid. */
  ownerUid?: string;
  createdAtLocal: number;
  updatedAtLocal: number;
  status: PostPublishStatus;
  attemptCount: number;
  text: string;
  subject: string;
  /** Academic context of the content, separate from public author identity. */
  track?: string;
  visibility: "global" | "group";
  groupId: string | null;
  group: PostPublishGroupSnapshot | null;
  /** New jobs carry immutable public identity attribution. Old persisted jobs
   * remain readable so a local queue is not destructively reset. */
  author: PostAuthorAttribution | CanonicalPostAuthor;
  attachments: PostPublishAttachment[];
  error?: SerializedPostPublishError;
  progress: PostPublishProgress;
  totalDurationMs?: number;
};

export type PostPublishQueueEnvelope = {
  schemaVersion: typeof POST_PUBLISH_SCHEMA_VERSION;
  jobs: PostPublishJob[];
};
